<template>
  <div class="app-version-manage">
    <el-card>
      <div class="filter-container">
        <span class="search-label">自定义检索：</span>
        <el-select size="small" v-model="searchType" @change="handleSearchTypeChange">
          <el-option label="按系统类型" value="1" />
          <el-option label="按时间" value="2" />
        </el-select>
        
        <!-- 按系统类型搜索 -->
        <el-select
          v-if="searchType === '1'"
          size="small"
          v-model="searchKeywords.os_type"
          placeholder="请选择"
          style="width: 130px; margin-left: 10px"
        >
          <el-option label="Android" value="1" />
          <el-option label="iOS" value="2" />
        </el-select>
        
        <!-- 按时间搜索 -->
        <div v-if="searchType === '2'" style="margin-left: 10px;">
          <span>开始时间：</span>
          <el-date-picker
            size="small"
            v-model="searchKeywords.begin_date"
            type="date"
            placeholder="开始时间"
            value-format="yyyy-MM-dd"
            style="width: 140px; margin-right: 10px"
          />
          <span>结束时间：</span>
          <el-date-picker
            size="small"
            v-model="searchKeywords.end_date"
            type="date"
            placeholder="结束时间"
            value-format="yyyy-MM-dd"
            style="width: 140px"
          />
        </div>

        <el-button size="small" @click="handleSearch" style="margin-left: 10px">搜索</el-button>
        <el-button size="small" @click="handleShowAll">全部</el-button>
        
        <div class="right-buttons">
          <el-button size="small" type="primary" @click="handleAdd">添加版本</el-button>
        </div>
      </div>
    </el-card>

    <el-card style="margin-top: 15px">
      <el-table
        ref="table"
        border
        size="small"
        :data="tableData.rows"
        :height="tableHeight"
        :header-cell-style="{ backgroundColor: '#f7f7f7', color: '#333' }"
      >
        <el-table-column show-overflow-tooltip prop="app_name" label="终端名称" width="120" align="center" />
        <el-table-column show-overflow-tooltip prop="os_type" label="终端类型" width="120" align="center" :formatter="formatOsType" />
        <el-table-column show-overflow-tooltip prop="version" label="版本号" width="120" align="center" />
        <el-table-column show-overflow-tooltip prop="upload_url" label="上传客户端" width="200" align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.upload_url" :title="scope.row.upload_url">
              {{ formatFileName(scope.row.upload_url) }}
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column show-overflow-tooltip prop="disabled" label="状态" width="60" align="center" :formatter="formatStatus" />
        <el-table-column show-overflow-tooltip prop="created_at" label="创建时间" width="180" align="center" :formatter="formatDate" />
        <el-table-column show-overflow-tooltip prop="updated_at" label="更新时间" width="180" align="center" :formatter="formatDate" />
        <el-table-column show-overflow-tooltip prop="note" label="备注" width="150" align="center" />
        <el-table-column label="操作" width="150" align="center">
          <template slot-scope="{ row }">
            <template v-if="row.disabled">
              <el-button size="mini" type="success" @click="handleToggleStatus(row, false)">启用</el-button>
            </template>
            <template v-else>
              <el-button size="mini" type="warning" @click="handleToggleStatus(row, true)">禁用</el-button>
              <el-button size="mini" type="primary" @click="handleEdit(row)">编辑</el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>

      <pagination
        :total="tableData.total"
        :page.sync="pagination.page"
        :rows.sync="pagination.rows"
        @pagination="handleGetList"
      />
    </el-card>

    <!-- 添加/编辑对话框 -->
    <VersionEditDialog
      :visible.sync="editVisible"
      :title="editTitle"
      :form-data="editFormData"
      :app-info="appInfo"
      @save="handleSave"
    />
  </div>
</template>

<script>
import pagination from '@/components/Pagination'
import VersionEditDialog from './VersionEditDialog.vue'
import { getAppVersionList, toggleAppVersionStatus } from '@/api/custom-frontend'

export default {
  name: 'AppVersionManage',
  components: {
    pagination,
    VersionEditDialog
  },
  props: {
    appInfo: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      tableHeight: 400,
      searchType: '1',
      searchKeywords: {
        os_type: '',
        begin_date: '',
        end_date: ''
      },
      pagination: {
        page: 1,
        rows: 15,
        total: 0
      },
      tableData: {
        rows: [],
        total: 0
      },
      editVisible: false,
      editTitle: '',
      editFormData: {}
    }
  },
  watch: {
    appInfo: {
      handler(val) {
        if (val && val.id) {
          this.handleGetList()
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.calculateTableHeight()
    window.addEventListener('resize', this.calculateTableHeight)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateTableHeight)
  },
  methods: {
    calculateTableHeight() {
      this.$nextTick(() => {
        const windowHeight = window.innerHeight
        this.tableHeight = windowHeight - 300
      })
    },
    
    handleSearchTypeChange() {
      // 重置搜索关键字
      this.searchKeywords = {
        os_type: '',
        begin_date: '',
        end_date: ''
      }
    },
    
    async handleGetList() {
      if (!this.appInfo.id) return
      
      try {
        const params = {
          app_id: this.appInfo.id,
          page: this.pagination.page,
          rows: this.pagination.rows
        }
        
        const res = await getAppVersionList(params)
        if (res && res.err_code === 0) {
          this.tableData = res
          this.pagination.total = res.total
        }
      } catch (error) {
        console.error('获取版本列表失败:', error)
      }
    },
    
    async handleSearch() {
      const params = {
        app_id: this.appInfo.id,
        page: 1,
        rows: this.pagination.rows
      }
      
      // 根据搜索类型添加对应的参数
      if (this.searchType === '1' && this.searchKeywords.os_type) {
        params.os_type = this.searchKeywords.os_type
      }
      
      if (this.searchType === '2') {
        if (this.searchKeywords.begin_date) {
          params.begin_date = Math.floor(new Date(this.searchKeywords.begin_date + ' 00:00:00').getTime() / 1000)
        }
        if (this.searchKeywords.end_date) {
          params.end_date = Math.floor(new Date(this.searchKeywords.end_date + ' 23:59:59').getTime() / 1000)
        }
      }
      
      try {
        const res = await getAppVersionList(params)
        if (res && res.err_code === 0) {
          this.tableData = res
          this.pagination.total = res.total
          this.pagination.page = 1
        }
      } catch (error) {
        console.error('搜索版本列表失败:', error)
      }
    },
    
    handleShowAll() {
      this.searchKeywords = {
        os_type: '',
        begin_date: '',
        end_date: ''
      }
      this.pagination.page = 1
      this.handleGetList()
    },
    
    handleAdd() {
      this.editTitle = '添加版本'
      this.editFormData = {}
      this.editVisible = true
    },
    
    handleEdit(row) {
      this.editTitle = '编辑版本'
      this.editFormData = { ...row }
      this.editVisible = true
    },
    
    async handleToggleStatus(row, disabled) {
      const action = disabled ? '禁用' : '启用'
      this.$confirm(`您确认想要${action}吗？`, '确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await toggleAppVersionStatus({
            id: row.id,
            disabled: disabled
          })
          
          if (res && res.err_code === 0) {
            this.$message.success('操作成功')
            this.handleGetList()
          } else {
            this.$message.error(res.err_msg || '操作失败')
          }
        } catch (error) {
          console.error('操作失败:', error)
          this.$message.error('操作失败')
        }
      }).catch(() => {
        // 取消操作
      })
    },
    
    handleSave() {
      this.editVisible = false
      this.handleGetList()
    },
    
    // 格式化方法
    formatOsType(row, column, cellValue) {
      const osTypeMap = {
        1: 'Android',
        2: 'iOS',
        4: 'web',
        8: 'h5',
        '1': 'Android',
        '2': 'iOS',
        '4': 'web',
        '8': 'h5'
      }
      return osTypeMap[cellValue] || '未知'
    },
    
    formatStatus(row, column, cellValue) {
      return cellValue ? '禁用' : '启用'
    },
    formatDate(row, column, cellValue) {
      if (!cellValue || +new Date(cellValue) < 0) {
        return '-'
      }
      return this.$day(cellValue).format('YYYY-MM-DD HH:mm:ss')
    },
    
    formatFileName(url) {
      if (!url) return '-'
      const fileName = url.split('/').pop()
      return fileName || '未知文件'
    }
  }
}
</script>

<style lang="less" scoped>
.app-version-manage {
  .filter-container {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    
    .search-label {
      margin-right: 10px;
      font-size: 14px;
    }
    
    .right-buttons {
      margin-left: auto;
      display: flex;
      align-items: center;
    }
  }
}
</style>